 

// src/components/Datastore.js

import React, { useEffect, useState } from 'react';
import {
  Pane,
  Heading,
  Button,
  Tablist,
  Tab,
  Dialog,
  TextInputField,
  TextareaField,
  FilePicker,
  toaster,
  IconButton,
  Menu,
  Popover,
  Text,
  Badge,
  Spinner,
} from 'evergreen-ui';
import FirestoreService, { DatastoreManagerService, AccountManagerService } from '../appservices/AppManager';
import useStore from '../appservices/store';
import { AddIcon, MoreIcon, DocumentIcon, DeleteIcon, EditIcon } from 'evergreen-ui';

const ACCOUNT_MANAGER_ENDPOINT = 'https://us-east1-milli-8fba8.cloudfunctions.net/aiit/acct/account';
const document_endpoint = 'https://us-east1-milli-8fba8.cloudfunctions.net/aiit/acct/document';
 
// Reusable EmptyState Component
const EmptyState = ({ title, description, onAdd, actionLabel, illustration }) => {
  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={300}
      background="tint2"
      borderRadius={8}
      padding={24}
      textAlign="center"
    >
      {illustration && (
        <img
          src={illustration}
          alt="No Data"
          width={100}
          height={100}
          style={{ marginBottom: 16 }}
        />
      )}
      <Heading size={600} marginBottom={8}>
        {title}
      </Heading>
      <Text size={500} color="muted" marginBottom={16}>
        {description}
      </Text>
      <Button appearance="primary" intent="success" iconBefore={AddIcon} onClick={onAdd}>
        {actionLabel}
      </Button>
    </Pane>
  );
};

// AddDocumentationModal Component
const AddDocumentationModal = ({ isShown, onCloseComplete }) => {
  const { user, datastoreId, addDocument, businessId } = useStore();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabs = ['Upload', 'Paste', 'Link'];
  const [file, setFile] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSaveDocument00 = async () => {
    if (!businessId || !datastoreId) {
      toaster.danger('Business ID or Datastore ID is missing.');
      return;
    }

    if (!title.trim()) {
      toaster.danger('Please provide a title for the document.');
      return;
    }

    if (
      (selectedIndex === 0 && !file) ||
      (selectedIndex === 1 && !textContent.trim()) ||
      (selectedIndex === 2 && !linkUrl.trim())
    ) {
      toaster.danger('Please provide the required information for the selected tab.');
      return;
    }

    setIsLoading(true);
    try {
      const firestoreService = new FirestoreService(
        `Datastore/${datastoreId}/Documents`
      );
      let docData = {
        title: title.trim(),
        tags: tags,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (selectedIndex === 0 && file) {
        const fileUrl = await firestoreService.uploadFile(user.uid, file, 'documentation');
        docData = { ...docData, type: 'upload', fileUrl, fileName: file.name };
      } else if (selectedIndex === 1 && textContent.trim()) {
        docData = { ...docData, type: 'paste', content: textContent.trim() };
      } else if (selectedIndex === 2 && linkUrl.trim()) {
        docData = { ...docData, type: 'link', content: linkUrl.trim() };
      }

      const docRef = await firestoreService.createOrUpdateDocument(null, docData);
      addDocument({ id: docRef, ...docData });
      toaster.success('Documentation added successfully.');

      // Notify DatastoreManager backend
      // try {
      //   await DatastoreManagerService.manageDocuments(businessId, datastoreId, 'add', {
      //     documentId: docRef,
      //     ...docData,
      //   });
      //   console.log('DatastoreManager notified successfully.');
      // } catch (notificationError) {
      //   console.error('Error notifying DatastoreManager:', notificationError);
      //   toaster.danger('Documentation added, but failed to notify the backend.');
      // }

      // Reset fields
      setFile(null);
      setTextContent('');
      setLinkUrl('');
      setTitle('');
      setTags([]);

      onCloseComplete();
    } catch (error) {
      console.error('Error adding documentation:', error);
      toaster.danger(error.message || 'Failed to add documentation.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDocument = async () => {
    if (!businessId || !datastoreId) {
      toaster.danger('Business ID or Datastore ID is missing.');
      return;
    }
  
    if (!title.trim()) {
      toaster.danger('Please provide a title for the document.');
      return;
    }
  
    if (
      (selectedIndex === 0 && !file) ||
      (selectedIndex === 1 && !textContent.trim()) ||
      (selectedIndex === 2 && !linkUrl.trim())
    ) {
      toaster.danger('Please provide the required information for the selected tab.');
      return;
    }
  
    setIsLoading(true);
    try {
      const firestoreService = new FirestoreService(
        `Datastore/${datastoreId}/Documents`
      );
      let docData = {
        title: title.trim(),
        tags: tags,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
  
      let fileUrl = '';
      if (selectedIndex === 0 && file) {
        fileUrl = await firestoreService.uploadFile(user.uid, file, 'documentation');
        docData = { ...docData, type: 'upload', fileUrl, fileName: file.name };
      } else if (selectedIndex === 1 && textContent.trim()) {
        docData = { ...docData, type: 'paste', content: textContent.trim() };
      } else if (selectedIndex === 2 && linkUrl.trim()) {
        docData = { ...docData, type: 'link', content: linkUrl.trim() };
      }
  
      const docRef = await firestoreService.createOrUpdateDocument(null, docData);
  
      // Send GCS URL, Firestore Document ID, and Authenticated User ID to the backend
      const payload = {
        userId: user.uid,
        documentId: docRef,
        gcsUrl: fileUrl,
      };
  
      await fetch(document_endpoint+'/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
  
      addDocument({ id: docRef, ...docData });
      toaster.success('Documentation added and backend notified successfully.');
  
      // Reset fields
      setFile(null);
      setTextContent('');
      setLinkUrl('');
      setTitle('');
      setTags([]);
  
      onCloseComplete();
    } catch (error) {
      console.error('Error adding documentation:', error);
      toaster.danger(error.message || 'Failed to add documentation.');
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <Dialog
      isShown={isShown}
      title="Add Documentation"
      onCloseComplete={() => {
        onCloseComplete();
        // Reset fields when dialog is closed
        setFile(null);
        setTextContent('');
        setLinkUrl('');
        setTitle('');
        setTags([]);
      }}
      onConfirm={handleSaveDocument}
      confirmLabel={isLoading ? 'Saving...' : 'Save'}
      isConfirmLoading={isLoading}
      intent="success"
    >
      {/* Title Field */}
      <TextInputField
        label="Title"
        placeholder="Enter document title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        marginBottom={16}
        required
      />

      {/* Tags Input */}
      <Pane marginBottom={16}>
        <Text>Tags (comma-separated)</Text>
        <TextInputField
          placeholder="e.g., manual, product_a, faq"
          value={tags.join(', ')}
          onChange={(e) => {
            const input = e.target.value;
            const newTags = input.split(',').map((tag) => tag.trim()).filter((tag) => tag);
            setTags(newTags);
          }}
        />
      </Pane>

      {/* Tab Navigation */}
      <Tablist marginBottom={16}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab}
            id={tab}
            onSelect={() => setSelectedIndex(index)}
            isSelected={index === selectedIndex}
            aria-controls={`panel-${tab}`}
            marginRight={8}
          >
            {tab}
          </Tab>
        ))}
      </Tablist>

      {/* Tab Content */}
      {selectedIndex === 0 && (
        <FilePicker
          multiple={false}
          onChange={(files) => setFile(files[0])}
          placeholder="Select the file here!"
          marginBottom={16}
        />
      )}
      {selectedIndex === 1 && (
        <TextareaField
          label="Paste your content"
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
          marginBottom={16}
          placeholder="Enter or paste your document content here..."
        />
      )}
      {selectedIndex === 2 && (
        <TextInputField
          label="Link URL"
          placeholder="https://example.com/document"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          marginBottom={16}
        />
      )}
    </Dialog>
  );
};

// DocumentationList Component
const DocumentationList = () => {
  const { documents, removeDocument, businessId, datastoreId, setIsAddDialogShown } = useStore();

  const handleDelete = async (document) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        const firestoreService = new FirestoreService(
          `Datastore/${datastoreId}/Documents`
        );
        await firestoreService.deleteDocument(document.id);

        if (document.type === 'upload' && document.fileUrl) {
          await firestoreService.deleteFile(document.fileUrl);
        }

        removeDocument(document.id);
        toaster.success('Documentation deleted successfully.');

        // Notify DatastoreManager backend about deletion
        // try {
        //   await DatastoreManagerService.manageDocuments(businessId, datastoreId, 'delete', {
        //     documentId: document.id,
        //   });
        //   console.log('DatastoreManager notified successfully about deletion.');
        // } catch (notificationError) {
        //   console.error('Error notifying DatastoreManager about deletion:', notificationError);
        //   toaster.danger('Documentation deleted, but failed to notify the backend.');
        // }
      } catch (error) {
        console.error('Error deleting document:', error);
        toaster.danger(error.message || 'Failed to delete documentation.');
      }
    }
  };

  // Render individual document item
  const renderDocumentItem = (document) => (
    <Pane
      key={document.id}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding={8}
      borderBottom="muted"
    >
      <Pane display="flex" alignItems="center">
        <DocumentIcon color="blue" marginRight={8} />
        <Text>
          {document.title}
          {document.type === 'upload' && ` (Uploaded File: ${document.fileName})`}
          {document.type === 'paste' && ` (Pasted Content)`}
          {document.type === 'link' && ` (Linked Document: ${document.content})`}
        </Text>
        {document.tags && document.tags.length > 0 && (
          <Pane display="flex" marginLeft={8}>
            {document.tags.map((tag, index) => (
              <Badge key={index} color="green" marginRight={4}>
                {tag}
              </Badge>
            ))}
          </Pane>
        )}
      </Pane>
      <Popover
        content={
          <Menu>
            <Menu.Group>
              <Menu.Item icon={DeleteIcon} intent="danger" onSelect={() => handleDelete(document)}>
                Delete
              </Menu.Item>
            </Menu.Group>
          </Menu>
        }
        position="bottom-right"
      >
        <IconButton icon={MoreIcon} appearance="minimal" />
      </Popover>
    </Pane>
  );

  // Handle the Add Documentation button click
  const handleAddDocumentation = () => {
    setIsAddDialogShown(true);
  };

  if (documents.length === 0) {
    return (
      <EmptyState
        title="No Documentation Available"
        description="You haven't added any documentation yet. Start by adding your first document to help the AI understand your context better."
        onAdd={handleAddDocumentation}
        actionLabel="Add Documentation"
        // illustration can be an SVG or image URL, replace with actual path if available
        illustration={null}
      />
    );
  }

  return (
    <Pane>
      {documents.map((document) => renderDocumentItem(document))}
    </Pane>
  );
};

// InstructionsEditor Component
const InstructionsEditor = () => {
  const { instructions, setInstructions, businessId, datastoreId } = useStore();
  const [content, setContent] = useState(instructions || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    if (!businessId || !datastoreId) {
      toaster.danger('Business ID or Datastore ID is missing.');
      return;
    }

    if (!content.trim()) {
      toaster.danger('Instructions cannot be empty.');
      return;
    }

    setIsSaving(true);
    try {
      const firestoreService = new FirestoreService(
        `Datastore/${datastoreId}/Instructions`
      );
      const instructionData = {
        content: content.trim(),
        updatedAt: new Date(),
      };

      // Save the instructions
      await firestoreService.createOrUpdateDocument('main', instructionData);
      setInstructions(content.trim());
      toaster.success('Instructions saved successfully.');

      // Notify DatastoreManager backend
      // try {
      //   console.log('Notifying DatastoreManager about documentation addition...', businessId, datastoreId);

      //   await DatastoreManagerService.manageInstructions(businessId, datastoreId, 'update', {
      //     content: content.trim(),
      //   });
      //   console.log('DatastoreManager notified successfully for instructions.');
      // } catch (notificationError) {
      //   console.error('Error notifying DatastoreManager for instructions:', notificationError);
      //   toaster.danger('Instructions saved, but failed to notify the backend.');
      // }

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving instructions:', error);
      toaster.danger(error.message || 'Failed to save instructions.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete the instructions?')) {
      setIsSaving(true);
      try {
        const firestoreService = new FirestoreService(
          `Datastore/${datastoreId}/Instructions`
        );
        await firestoreService.deleteDocument('main');
        setInstructions('');
        setContent('');
        toaster.success('Instructions deleted successfully.');

        // Notify DatastoreManager backend about deletion
        // try {
        //   await DatastoreManagerService.manageInstructions(businessId, datastoreId, 'delete', {});
        //   console.log('DatastoreManager notified successfully about instructions deletion.');
        // } catch (notificationError) {
        //   console.error('Error notifying DatastoreManager about instructions deletion:', notificationError);
        //   toaster.danger('Instructions deleted, but failed to notify the backend.');
        // }
      } catch (error) {
        console.error('Error deleting instructions:', error);
        toaster.danger(error.message || 'Failed to delete instructions.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!instructions && !isEditing) {
    return (
      <EmptyState
        title="No Instructions Available"
        description="You haven't set any instructions yet. Adding instructions will help the AI understand your requirements better."
        onAdd={() => setIsEditing(true)}
        actionLabel="Add Instructions"
        // illustration can be an SVG or image URL, replace with actual path if available
        illustration={null}
      />
    );
  }

  return (
    <Pane>
      {isEditing ? (
        <>
          <TextareaField
            label="Instructions"
            description="These instructions will guide the AI's behavior."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter the instructions for the AI..."
            marginBottom={16}
          />
          <Pane display="flex" alignItems="center">
            <Button
              appearance="primary"
              intent="success"
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save Instructions
            </Button>
            <Button
              marginLeft={16}
              onClick={() => {
                setIsEditing(false);
                setContent(instructions);
              }}
            >
              Cancel
            </Button>
          </Pane>
        </>
      ) : (
        <>
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Heading size={600}>Instructions</Heading>
            <Popover
              content={
                <Menu>
                  <Menu.Group>
                    <Menu.Item icon={EditIcon} onSelect={handleEdit}>
                      Edit
                    </Menu.Item>
                    <Menu.Item icon={DeleteIcon} intent="danger" onSelect={handleDelete}>
                      Delete
                    </Menu.Item>
                  </Menu.Group>
                </Menu>
              }
              position="bottom-right"
            >
              <IconButton icon={MoreIcon} appearance="minimal" />
            </Popover>
          </Pane>
          <Pane padding={16} background="tint2" borderRadius={8} marginTop={8}>
            <Text>{instructions}</Text>
          </Pane>
        </>
      )}
    </Pane>
  );
};

// DatastoreBoard Component
const DatastoreBoard = () => {
  const {
    user,
    documents,
    setDocuments,
    instructions,
    setInstructions,
    setDatastoreId,
    datastoreId,
    businessId,
    setBusinessId,
    isAddDialogShown,
    setIsAddDialogShown,
  } = useStore();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const initializeDatastore = async () => {
      if (!user) {
        console.log('User is not logged in. Skipping initialization.');
        setIsInitializing(false);
        return;
      }

      try {
        console.log('Initializing datastore for user:', user.uid);

        // Step 1: Fetch the user profile (already fetched in App.js and stored in userProfile)
        // Assuming userProfile contains businessId and datastoreId
        // If not, adjust accordingly

        // Step 2: If the businessId exists, fetch the business profile
        if (businessId) {
          console.log(`Fetching business profile for businessId: ${businessId}`);
          // Business profile is already fetched and stored in businessProfile
          // If additional data is needed, fetch here

          // No action needed as businessProfile is already in store
        } else {
          console.log('No businessId found. Creating a new user account...');
        }

        // Step 3: If either businessId or datastoreId is missing, create a new user account
        if (!businessId || !datastoreId) {
          console.log('No businessId or datastoreId found. Creating a new user account...');
          const userData = {
            uid: user.uid,
            firstName: user.displayName?.split(' ')[0] || '',
            lastName: user.displayName?.split(' ')[1] || '',
            email: user.email,
            // Additional user data as needed
          };

          // Call AccountManager to create a new user account
          const accountData = await AccountManagerService.createUserAccount(userData);
          console.log('Account data:', accountData);

          if (accountData.error) {
            throw new Error(accountData.error);
          }

          setBusinessId(accountData.businessId);
          setDatastoreId(accountData.datastoreId);
        }

        // Step 4: Fetch documents and instructions
        if (businessId && datastoreId) {
          await Promise.all([
            fetchDocuments(datastoreId),
            fetchInstructions(datastoreId),
          ]);
        }
      } catch (error) {
        console.error('Error initializing datastore:', error);
        toaster.danger('Failed to initialize datastore.');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeDatastore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, businessId, datastoreId, setBusinessId, setDatastoreId, setDocuments, setInstructions]);

  const fetchDocuments = async (datastoreId) => {
    try {
      const firestoreServiceDocuments = new FirestoreService(
        `Datastore/${datastoreId}/Documents`
      );
      const docs = await firestoreServiceDocuments.getAllDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toaster.danger('Failed to fetch documents.');
    }
  };

  const fetchInstructions = async (datastoreId) => {
    try {
      const firestoreServiceInstructions = new FirestoreService(
        `Datastore/${datastoreId}/Instructions`
      );
      const doc = await firestoreServiceInstructions.getDocument('main');
      if (doc) {
        setInstructions(doc.content);
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
      toaster.danger('Failed to fetch instructions.');
    }
  };

  const tabs = ['Documentation', 'Instructions'];

  if (isInitializing) {
    return (
      <Pane padding={16}>
        <Heading size={700} marginBottom={24}>
          Datastore Management
        </Heading>
        <Pane display="flex" alignItems="center">
          <Spinner />
          <Text marginLeft={8}>Initializing datastore...</Text>
        </Pane>
      </Pane>
    );
  }

  return (
    <Pane padding={16}>
      <Heading size={700} marginBottom={24}>
        Datastore Management
      </Heading>
      <Tablist marginBottom={16} flexBasis={240}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab}
            id={tab}
            onSelect={() => setSelectedIndex(index)}
            isSelected={index === selectedIndex}
            aria-controls={`panel-${tab}`}
            marginRight={8}
          >
            {tab}
          </Tab>
        ))}
      </Tablist>

      {selectedIndex === 0 && (
        <Pane>
          <Button
            appearance="primary"
            intent="success"
            marginBottom={16}
            onClick={() => setIsAddDialogShown(true)}
            iconBefore={AddIcon}
          >
            Add Documentation
          </Button>
          <DocumentationList />
        </Pane>
      )}

      {selectedIndex === 1 && (
        <Pane>
          <InstructionsEditor />
        </Pane>
      )}

      <AddDocumentationModal
        isShown={isAddDialogShown}
        onCloseComplete={() => setIsAddDialogShown(false)}
      />
    </Pane>
  );
};

export default DatastoreBoard;
